import {useIntl} from 'react-intl';

import {EmoticonPlusPlusOutlineIcon} from '@time-webkit/all/icons/emoticon-plus-outline';

import {BookmarkOutlineIcon} from '@time-webkit/all/icons/bookmark-outline';
import {ReplyOutlineIcon} from '@time-webkit/all/icons/reply_outline';

import {EmoticonOutlineIcon} from '@time-webkit/all/icons/emoticon-outline';
import {TrashIcon} from '@time-webkit/all/icons/trash';
import {ContentCopy} from '@time-webkit/all/icons/content-copy';
import {LinkVariantIcon} from '@time-webkit/all/icons/link-variant';
import {BookmarkIcon} from '@time-webkit/all/icons/bookmark';
import {ConversationIcon} from '@time-webkit/all/icons/conversation';
import {MessageMinusOutlineIcon} from '@time-webkit/all/icons/message-minus-outline';
import {MessageCheckOutlineIcon} from '@time-webkit/all/icons/message-check-outline';
import {MarkAsUnreadIcon} from '@time-webkit/all/icons/mark-as-unread';
import {PinIcon} from '@time-webkit/all/icons/pin';
import {PinOutlineIcon} from '@time-webkit/all/icons/pin-outline';
import {JumpIcon} from '@time-webkit/all/icons/jump';

import {markChannelLinkClicked, markThreadLinkClicked} from '@time-webkit/performance';
import {type EventParametersMap} from '@time-webkit/statist';

import {ReactionsModal} from 'components/reactions_modal';
import DeletePostModal from 'components/delete_post_modal';

import * as PostUtils from 'utils/post_utils';
import {Locations, ModalIdentifiers} from 'utils/constants';
import {copyToClipboard} from 'utils/copy_to_clipboard';
import {browserHistory} from 'utils/browser_history';
import {reportErrorToSentry} from 'utils/sentry';

import {type PostMenuItems, type UsePostMenuItemsParams} from './types';
import {usePostMenuData} from './use_post_menu_data';
import {createEmptyPostMenuItems, isCenterOrThreadLocation, isRHSLocation, isThreadLocation} from './utils';
import {usePostMenuEditItem} from './use_post_menu_edit_item';
import {usePostMenuPluginItem} from './use_post_menu_plugin_item';
import {usePostMenuActions} from './post_context_menu/post_menu_actions';

export function usePostMenuItems(params: UsePostMenuItemsParams) {
    const {post, location, onToggleEmojiPicker, onLogPostAction} = params;

    const {formatMessage} = useIntl();
    const postMenuData = usePostMenuData(params);
    const actions = usePostMenuActions();

    const isActivityFeedLocation = location === Locations.ACTIVITY_FEED;
    const isSystemMessage = PostUtils.isSystemMessage(post);
    const items: PostMenuItems = createEmptyPostMenuItems();

    function logAction(
        action: EventParametersMap['app.postAction']['action'],
        event: React.MouseEvent | KeyboardEvent,
    ) {
        const inputType = event.type === 'keydown' ? 'hotkey' : params.triggerType;
        onLogPostAction(action, inputType);
    }

    function getMarkSourceType() {
        if (location === Locations.SEARCH) {
            return 'search';
        }

        if (isRHSLocation(location)) {
            return 'sidebar';
        }

        return 'link';
    }

    items.EDIT = usePostMenuEditItem(params, postMenuData, actions, logAction);
    items.PLUGINS = usePostMenuPluginItem(params, postMenuData, logAction);

    if (!isSystemMessage) {
        items.COPY_LINK = {
            attributes: {id: `permalink_${post.id}`, 'data-qa': 'post_permalink'},
            text: formatMessage({id: 'post_info.permalink', defaultMessage: 'Copy message link'}),
            icon: <LinkVariantIcon size={18} />,
            shortcut: 'K',
            action: async (event) => {
                const link = `${postMenuData.teamUrl}/pl/${post.id}`;
                try {
                    await copyToClipboard(link);
                    logAction('linkCopy', event);
                } catch (error) {
                    reportErrorToSentry(new Error(`Menu: can't copy ${link} due to ${error}`));
                }
            },
        };

        items.COPY_TEXT = {
            attributes: {id: `copy_${post.id}`, 'data-qa': 'post_copy'},
            text: formatMessage({id: 'post_info.copy', defaultMessage: 'Copy Text'}),
            icon: <ContentCopy width={18} height={18} />,
            shortcut: 'C',
            action: async (event) => {
                await copyToClipboard(post.message);
                logAction('copy', event);
            },
        };

        if (postMenuData.enableEmojiPicker && onToggleEmojiPicker) {
            items.ADD_EMOJI = {
                attributes: {id: `add_emoji_${post.id}`},
                text: formatMessage({id: 'rhs_root.mobile.add_reaction', defaultMessage: 'Add Reaction'}),
                icon: <EmoticonPlusPlusOutlineIcon width={18} height={18} />,
                preventClose: true,
                action: (event) => {
                    logAction('addReaction', event);
                    onToggleEmojiPicker(event as React.MouseEvent);
                },
            };
        }

        if (postMenuData.hasReaction && !postMenuData.isArchivedChannel) {
            const reactionsModalData = {
                modalId: ModalIdentifiers.MESSAGE_REACTIONS,
                dialogType: ReactionsModal,
                dialogProps: {postId: post.id},
            };

            items.REACTIONS = {
                attributes: {id: `reaction_post_${post.id}`},
                text: formatMessage({
                    id: 'post_info.reactions',
                    defaultMessage: 'See reactions',
                }),
                icon: <EmoticonOutlineIcon width={18} height={18} />,
                action: (event) => {
                    logAction('reactions', event);
                    actions.openModal(reactionsModalData);
                },
            };
        }
    }

    if (!isSystemMessage && !postMenuData.isReadOnlyChannel) {
        if (postMenuData.canReply && isCenterOrThreadLocation(location)) {
            items.REPLY = {
                attributes: {id: `reply_${post.id}`, 'data-qa': 'post_reply'},
                text: formatMessage({id: 'post_info.reply', defaultMessage: 'Reply'}),
                icon: <ReplyOutlineIcon width={18} height={18} />,
                action: (event) => {
                    const message = {type: 'reply_to_message', post, isRHS: isRHSLocation(location), location};
                    window.postMessage(message, window.location.origin);
                    logAction('reply', event);
                },
            };
        }

        if (!isThreadLocation(location)) {
            items.DISCUSS_MESSAGE = {
                attributes: {id: `discuss_message_${post.id}`, 'data-qa': 'post_reply_in_thread'},
                text: formatMessage({id: 'post_info.reply_in_thread', defaultMessage: 'Discuss message'}),
                icon: <ConversationIcon width={18} height={18} />,
                shortcut: 'R',
                action: (event) => {
                    event.preventDefault();

                    markThreadLinkClicked({
                        detail: {
                            sourceType: getMarkSourceType(),
                            channelId: post.channel_id,
                            channelName: postMenuData.channelName,
                            channelType: postMenuData.channelType,
                        },
                    });

                    if (isCenterOrThreadLocation(location)) {
                        actions.selectPost(post);
                    } else {
                        actions.selectPostFromRightHandSideSearch(post);
                    }

                    logAction('discuss', event);
                },
            };
        }

        if (isCenterOrThreadLocation(location)) {
            let text;

            if (postMenuData.isFollowingThread && postMenuData.threadReplyCount > 0) {
                text = formatMessage({id: 'threading.threadMenu.unfollow', defaultMessage: 'Unfollow thread'});
            } else if (postMenuData.isFollowingThread && !postMenuData.threadReplyCount) {
                text = formatMessage(({
                    id: 'threading.threadMenu.unfollowMessage',
                    defaultMessage: 'Unfollow message',
                }));
            } else if (!postMenuData.isFollowingThread && postMenuData.threadReplyCount > 0) {
                text = formatMessage({id: 'threading.threadMenu.follow', defaultMessage: 'Follow thread'});
            } else {
                text = formatMessage({id: 'threading.threadMenu.followMessage', defaultMessage: 'Follow message'});
            }

            items.POST_FOLLOW = {
                attributes: {id: `follow_post_thread_${post.id}`, 'data-qa': 'post_follow'},
                shortcut: 'F',
                icon: postMenuData.isFollowingThread ? (
                    <MessageMinusOutlineIcon width={18} height={18} />
                ) : (
                    <MessageCheckOutlineIcon width={18} height={18} />
                ),
                text,
                action: (event) => {
                    actions.changeThreadFollowOnServer({
                        userId: postMenuData.currentUser.id,
                        teamId: postMenuData.teamId,
                        threadId: postMenuData.threadId,
                        following: !postMenuData.isFollowingThread,
                    });
                    logAction(postMenuData.isFollowingThread ? 'unsubscribe' : 'subscribe', event);
                },
            };
        }

        if (!postMenuData.isArchivedChannel && isCenterOrThreadLocation(location)) {
            items.MARK_AS_UNREAD = {
                attributes: {id: `unread_post_${post.id}`, 'data-qa': 'post_unread'},
                text: formatMessage({id: 'post_info.unread', defaultMessage: 'Mark as Unread'}),
                icon: <MarkAsUnreadIcon width={18} height={18} />,
                shortcut: 'U',
                action: (event) => {
                    event.preventDefault();
                    actions.markPostAsUnread(post, location);
                    logAction('unread', event);
                },
            };
        }

        if (postMenuData.isFlaggedPost) {
            items.REMOVE_FROM_SAVED = {
                attributes: {id: `unflag_${post.id}`},
                text: formatMessage({id: 'rhs_root.mobile.unflag', defaultMessage: 'Remove from Saved'}),
                icon: <BookmarkIcon width={18} height={18} />,
                shortcut: 'S',
                action: (event) => {
                    actions.unflagPost(post.id);
                    logAction('unsave', event);
                },
            };
        }

        if (!postMenuData.isFlaggedPost) {
            items.SAVE = {
                attributes: {id: `flag_${post.id}`},
                text: formatMessage({id: 'rhs_root.mobile.flag', defaultMessage: 'Save'}),
                icon: <BookmarkOutlineIcon width={18} height={18} />,
                shortcut: 'S',
                action: (event) => {
                    actions.flagPost(post.id);
                    logAction('save', event);
                },
            };
        }

        if (!isActivityFeedLocation && !postMenuData.isArchivedChannel && post.is_pinned) {
            items.UNPIN = {
                attributes: {id: `unpin_post_${post.id}`, 'data-qa': 'post_pin'},
                text: formatMessage({id: 'post_info.unpin', defaultMessage: 'Unpin'}),
                icon: <PinIcon width={18} height={18} />,
                shortcut: 'P',
                action: (event) => {
                    actions.unpinPost(post.id);
                    logAction('unpin', event);
                },
            };
        }

        if (!isActivityFeedLocation && !postMenuData.isArchivedChannel && !post.is_pinned) {
            items.PIN = {
                attributes: {id: `pin_post_${post.id}`, 'data-qa': 'post_pin'},
                text: formatMessage({id: 'post_info.pin', defaultMessage: 'Pin'}),
                icon: <PinOutlineIcon width={18} height={18} />,
                shortcut: 'P',
                action: (event) => {
                    actions.pinPost(post.id);
                    logAction('pin', event);
                },
            };
        }
    }

    if (!postMenuData.isReadOnlyChannel && postMenuData.canDelete) {
        items.DELETE = {
            attributes: {id: `delete_post_${post.id}`, 'data-qa': 'post_delete'},
            text: formatMessage({id: 'post_info.del', defaultMessage: 'Delete'}),
            icon: <TrashIcon width={18} height={18} />,
            shortcut: 'delete',
            isDangerous: true,
            action: (event) => {
                event.preventDefault();

                const deletePostModalData = {
                    modalId: ModalIdentifiers.DELETE_POST,
                    dialogType: DeletePostModal,
                    dialogProps: {
                        post,
                        isRHS: isRHSLocation(location),
                        isActivityFeed: isActivityFeedLocation,
                    },
                };

                actions.openModal(deletePostModalData);
                logAction('delete', event);
            },
        };
    }

    items.JUMP = {
        attributes: {id: `go_to_message_${post.id}`},
        text: formatMessage({id: 'rhs_card.jump', defaultMessage: 'Jump'}),
        icon: <JumpIcon width={18} height={18} />,
        action: (event) => {
            const markLinkClicked = post.root_id ? markThreadLinkClicked : markChannelLinkClicked;

            markLinkClicked({
                detail: {
                    sourceType: getMarkSourceType(),
                    channelId: post.channel_id,
                    channelName: postMenuData.channelName,
                    channelType: postMenuData.channelType,
                },
            });

            browserHistory.push(`/${postMenuData.teamName}/pl/${post.id}`);
            logAction('jump', event);
        },
    };

    return items;
}
